import { Head } from '@components/common/head';
import React, { useEffect, useState } from 'react';
import { NavBarContainer } from '@components/navigation';
import styled from '@emotion/styled';
import { ErrorThemeProps, Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { mq } from '@styles/breakpoints';
import { Formik, FormikProps, FormikValues } from 'formik';
import {
  FormState,
  initialFormState,
  myBookingLoginFormInitialValues,
  MyBookingLoginFormPayload,
  MyBookingLoginFormValues
} from '@model/forms';
import { validateMyBookingLoginForm } from '@model/forms/validation';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { useDispatch, useSelector } from 'react-redux';
import { ModalType, showModal } from '@state/modal/modalOperations';
import { FormikTextField } from '@components/common/forms';
import { getErrors } from '@util/forms';
import { Button, ButtonColor, ButtonSize, ButtonVariant } from '@components/material-ui';
import { MyBookingLoginFormErrors } from '@components/modal/form-error/mmb-login-form-error';
import {
  getBookingErrorDetails,
  getIsMyBookingAgent,
  getIsMyBookingError,
  getIsMyBookingLocked,
  getIsMyBookingPending,
  requestBookingToken,
  resetBookingDetails,
  resetBookingPending
} from '@state/mmb';
import { Icon } from '@components/common/generic-icon';
import { Icons } from '@model/common/icons';
import { Grid } from '@mui/material';
import {
  DateDayMonthField,
  DateField,
  DateFieldsWrapper,
  DateYearField,
  ErrorLabel,
  FieldLabel,
  FormRowGrid,
  isValidFormNumber
} from '@styles/forms';
import { withTheme } from '@emotion/react';
import { AppVariant } from '@model/config/brands';
import useFormUrlSync from '@components/hooks/useFormUrlSync';
import { IMAGE_FLEX_URL } from '@util/images';

/* ***************** *
 *       Types       *
 * ***************** */
export interface MyBookingLoginPageLayoutProps {
  theme: Theme;
}

const LOGIN_HERO_URL = `${IMAGE_FLEX_URL}/cms/my-booking/my-booking-hero-Baobab_Beach_Resort.jpg`;

/* ****************** *
 *       Styles       *
 * ****************** */

const MyBookingLoginContainer = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  backgroundColor: theme.custom.colors.group10.lighter,
  minHeight: '100vh'
}));

const MyBookingLoginFormContainer = styled.div({
  display: 'flex',
  alignSelf: 'stretch',
  flex: '1 0 auto'
});

const MyBookingLoginForm = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  width: '100%',

  [mq.small]: {
    flex: '50%'
  }
});

const MyBookingLoginHero = styled.div(({ imageUrl }: { imageUrl: string }) => ({
  display: 'none',

  [mq.small]: {
    display: 'block',
    minHeight: '100%',
    backgroundImage: `url(${imageUrl})`,
    backgroundSize: 'cover',
    flex: '50%'
  }
}));

const MyBookingFormInner = styled.div(({ theme }: ThemeProps) => ({
  flex: 'none',
  width: '100%',
  maxWidth: 390,
  padding: theme.custom.spacing.large
}));

const Heading: any = styled.h3(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h3 as any),
  color: theme.custom.colors.black,
  marginTop: theme.custom.spacing.xLarge,
  marginBottom: theme.custom.spacing.xLarge
}));

const FormikTextFieldStyled = styled(FormikTextField)(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.large
}));

const LogInButton = styled(Button)(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.large
}));

const LockedOut = styled.div(({ theme }: ThemeProps) => ({
  ['i']: {
    fontSize: '2rem',
    color: theme.custom.colors.group4.base,
    marginRight: theme.custom.spacing.xSmall,
    verticalAlign: 'text-top'
  }
}));

const FormInnerWrapper: any = styled.div(({ theme }: ThemeProps) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',

  [mq.small]: {
    maxWidth: theme.custom.contentSizes.maxContentWidth * 0.5
  }
}));

const FormRowGridStyled = styled(FormRowGrid)(({ theme }: ThemeProps) => ({
  ['&.MuiGrid-container']: {
    marginTop: theme.custom.spacing.medium
  }
}));

export const DOBLabel: any = styled.div(({ theme, isError }: ErrorThemeProps) => ({
  ...(theme.custom.typography.labels.attribute as any),
  color: isError ? theme.custom.states.error.dark : theme.custom.colors.group4.light
}));

const Subtitle = styled.p({
  display: 'flex',
  alignItems: 'center'
});

const WhiteLabelMessage: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

/* ************************* *
 *    MyBookingPageLayout    *
 * ************************* */

export const MyBookingLoginPageLayoutComponent = ({ theme }: MyBookingLoginPageLayoutProps) => {
  const t = useI18NextContext();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const dispatch = useDispatch();
  const isError: boolean = useSelector(getIsMyBookingError);
  const isPending: boolean = useSelector(getIsMyBookingPending);
  const isLocked: boolean = useSelector(getIsMyBookingLocked);
  const isAgentBooking: boolean = useSelector(getIsMyBookingAgent);
  const bookingErrorDetails = useSelector(getBookingErrorDetails);
  const isWhiteLabel = AppVariant.isWhitelabel();
  const hasSearchString = !!window.location.search;

  /* *** STATE **** */
  const [formValues, setFormValues] = useState<MyBookingLoginFormValues>(myBookingLoginFormInitialValues);
  const [formState, setFormState] = useState<FormState>(initialFormState);
  const [bookingRef, setBookingRef] = useState('');
  const [departureYear, setDepartureYear] = useState('');
  const [departureMonth, setDepartureMonth] = useState('');
  const [departureDay, setDepartureDay] = useState('');
  const [isDeepLink, setIsDeepLink] = useState(false);

  useEffect(() => {
    if (isDeepLink) return;
    const urlSearchString = window.location.search;
    const searchParams = new URLSearchParams(urlSearchString);
    const bookingId = searchParams.get('booking_reference') ?? '';
    const lastName = '';
    const day = searchParams.get('departure_day') ?? '';
    const month = searchParams.get('departure_month') ?? '';
    const year = searchParams.get('departure_year') ?? '';

    setBookingRef(bookingId);
    setDepartureYear(year);
    setDepartureMonth(month);
    setDepartureDay(day);

    if (bookingId.length > 0 || day.length > 0 || month.length > 0 || year.length > 0) {
      setFormValues({
        bookingId: bookingId,
        lastName: lastName,
        departureDate: {
          day: day,
          month: month,
          year: year
        }
      });
      setFormState({ isValid: true, isTouched: true });
      setIsDeepLink(true);
    }
  }, []);

  useEffect(() => {
    if (bookingErrorDetails) {
      const { data } = bookingErrorDetails;
      if (data) {
        const { tenant_name: tenantName } = data;
        if (tenantName) {
          dispatch(showModal(ModalType.MMB_BOOKING_DOMAIN_ERROR, { tenantName, persist: true }));
        }
      }
    }
  }, [bookingErrorDetails]);

  /* *** LOGIC *** */
  const handleValidate = (values: MyBookingLoginFormValues) => {
    const formValidation = validateMyBookingLoginForm(values);
    if (isDeepLink || hasSearchString) {
      setFormValues({
        bookingId: values.bookingId || bookingRef,
        lastName: values.lastName,
        departureDate: {
          day: values.departureDate.day || departureDay,
          month: values.departureDate.month || departureMonth,
          year: values.departureDate.year || departureYear
        }
      });
    } else {
      setFormValues(values);
    }
    setFormState({ isValid: formValidation.isValid, isTouched: true });

    return formValidation.errors;
  };

  const submitForm = () => {
    if (formState.isTouched) {
      if (formState.isValid) {
        getBookingDetails(formValues);
      } else {
        onSubmitError(formValues);
      }
    }
  };

  const getBookingDetails = async (formValues: MyBookingLoginFormValues) => {
    if (!executeRecaptcha) {
      return;
    }

    const reCaptcha = await executeRecaptcha('mmblogin');
    const { bookingId, lastName, departureDate } = formValues;

    const payload: MyBookingLoginFormPayload = {
      bookingId,
      lastName,
      departureDate: `${departureDate.year}-${departureDate.month}-${departureDate.day}`,
      reCaptcha
    };

    dispatch(requestBookingToken(payload));
  };

  const onSubmitError = (values: FormikValues) => {
    const contactFormErrors: MyBookingLoginFormErrors = {
      values
    };
    dispatch(showModal(ModalType.MMB_LOGIN_FORM_ERROR, contactFormErrors));
  };

  const resetError = () => {
    return isPending ? dispatch(resetBookingPending()) : dispatch(resetBookingDetails());
  };

  return (
    <MyBookingLoginContainer>
      <Head metaTitle={t('seo__my-booking-page--title')} metaDescription={t('seo__my-booking-page--description')} />
      <NavBarContainer />
      <MyBookingLoginFormContainer>
        <MyBookingLoginForm>
          <FormInnerWrapper>
            <MyBookingFormInner>
              <Heading>{t('seo__my-booking-page--page-title')}</Heading>
              {!isWhiteLabel && (
                <>
                  {!isPending && !isError && !isLocked && !isAgentBooking && (
                    <>
                      <Formik
                        initialValues={formValues}
                        onSubmit={() => {}}
                        validate={handleValidate}
                        isInitialValid={false}
                      >
                        {({
                          touched,
                          errors,
                          handleChange,
                          handleBlur,
                          values,
                          setValues
                        }: FormikProps<FormikValues>) => {
                          const handleDepartureDateFormatting = (event: any, fieldName: string) => {
                            const targetValue = parseInt(event.target.value);
                            if (!isNaN(targetValue)) {
                              values.departureDate[fieldName] = targetValue < 10 ? `0${targetValue}` : targetValue;
                              handleBlur(event);
                            } else {
                              handleBlur(event);
                            }
                          };

                          const handleNumberInput = (event: any) => {
                            const value: string = event.target.value;
                            if (isValidFormNumber(value)) {
                              handleChange(event);
                            }
                          };

                          const fieldMappings = [
                            { formPath: 'bookingId', urlValue: bookingRef },
                            { formPath: 'departureDate.day', urlValue: departureDay },
                            { formPath: 'departureDate.month', urlValue: departureMonth },
                            { formPath: 'departureDate.year', urlValue: departureYear }
                          ];

                          useFormUrlSync(
                            { values, setValues },
                            [bookingRef, departureDay, departureMonth, departureYear],
                            fieldMappings
                          );

                          return (
                            <>
                              <FormikTextFieldStyled
                                error={getErrors(touched, errors, 'bookingId')}
                                name={'bookingId'}
                                placeholder={'0000000'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                label={'form__field-label--booking-id'}
                                value={values.bookingId}
                                type={'number'}
                                maxLength={7}
                              />
                              <FormikTextFieldStyled
                                error={getErrors(touched, errors, 'lastName')}
                                placeholder={'form__field-placeholder--lead-surname'}
                                name={'lastName'}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                label={'form__field-label--lead-surname'}
                                value={values.lastName}
                              />

                              <FormRowGridStyled container spacing={theme!.custom.gridSpacing.small}>
                                <Grid item xs={12}>
                                  <FieldLabel>{t('form__field-label--departure-date')}</FieldLabel>
                                  <DateFieldsWrapper>
                                    <DateField>
                                      <DOBLabel isError={!!getErrors(touched, errors, 'departureDate.day')}>
                                        {t('common__label--day')}
                                      </DOBLabel>
                                      <DateDayMonthField
                                        placeholder={'dd'}
                                        name="departureDate.day"
                                        onChange={handleNumberInput}
                                        maxLength="2"
                                        onBlur={(event: any) => handleDepartureDateFormatting(event, 'day')}
                                        value={values.departureDate.day}
                                        inputmode="numeric"
                                        pattern="[0-9]*"
                                        isError={!!getErrors(touched, errors, 'departureDate.day')}
                                      />
                                    </DateField>

                                    <DateField>
                                      <DOBLabel isError={!!getErrors(touched, errors, 'departureDate.month')}>
                                        {t('common__label--month')}
                                      </DOBLabel>
                                      <DateDayMonthField
                                        placeholder={'mm'}
                                        name="departureDate.month"
                                        onChange={handleNumberInput}
                                        maxLength="2"
                                        onBlur={(event: any) => handleDepartureDateFormatting(event, 'month')}
                                        value={values.departureDate.month}
                                        inputmode="numeric"
                                        pattern="[0-9]*"
                                        isError={!!getErrors(touched, errors, 'departureDate.month')}
                                      />
                                    </DateField>

                                    <DateField>
                                      <DOBLabel isError={!!getErrors(touched, errors, 'departureDate.year')}>
                                        {t('common__label--year')}
                                      </DOBLabel>
                                      <DateYearField
                                        placeholder={'yyyy'}
                                        name="departureDate.year"
                                        onChange={handleNumberInput}
                                        onBlur={handleBlur}
                                        maxLength="4"
                                        value={values.departureDate.year}
                                        inputmode="numeric"
                                        pattern="[0-9]*"
                                        isError={!!getErrors(touched, errors, 'departureDate.year')}
                                      />
                                    </DateField>
                                  </DateFieldsWrapper>
                                  <ErrorLabel>
                                    {(touched as any).departureDate?.day &&
                                      (touched as any).departureDate?.month &&
                                      (touched as any).departureDate?.year &&
                                      t(
                                        getErrors(touched, errors, 'departureDate.day') ||
                                          getErrors(touched, errors, 'departureDate.month') ||
                                          getErrors(touched, errors, 'departureDate.year')
                                      )}
                                  </ErrorLabel>
                                </Grid>
                              </FormRowGridStyled>
                              <LogInButton
                                variant={ButtonVariant.CONTAINED}
                                color={ButtonColor.SECONDARY}
                                label={'form__field-label--log-in'}
                                size={ButtonSize.SMALL}
                                onClick={submitForm}
                              />
                            </>
                          );
                        }}
                      </Formik>
                    </>
                  )}
                  {isPending && (
                    <>
                      <p>{t('mmb-booking-pending--text')}</p>
                      <LogInButton
                        variant={ButtonVariant.CONTAINED}
                        color={ButtonColor.SECONDARY}
                        label={'mmb-booking-fetch__error--button-label'}
                        size={ButtonSize.SMALL}
                        onClick={resetError}
                      />
                    </>
                  )}
                  {isError && (
                    <>
                      <p>{t('mmb-booking-fetch__error--text')}</p>
                      <p>{t('mmb-booking-fetch__travel-agent-error-text')}</p>
                      <LogInButton
                        variant={ButtonVariant.CONTAINED}
                        color={ButtonColor.SECONDARY}
                        label={'mmb-booking-fetch__error--button-label'}
                        size={ButtonSize.SMALL}
                        onClick={resetError}
                      />
                    </>
                  )}
                  {isLocked && (
                    <LockedOut>
                      <Subtitle>
                        <Icon name={Icons.LOCK} />
                        <strong>{t('mmb-booking-locked--title')}</strong>
                      </Subtitle>
                      <p>{t('mmb-booking-locked--text')}</p>
                      <LogInButton
                        variant={ButtonVariant.CONTAINED}
                        color={ButtonColor.SECONDARY}
                        label={'mmb-booking-fetch__error--button-label'}
                        size={ButtonSize.SMALL}
                        onClick={resetError}
                      />
                    </LockedOut>
                  )}
                  {isAgentBooking && (
                    <LockedOut>
                      <Subtitle>
                        <Icon name={Icons.STOP} />
                        <strong>{t('mmb__agent-booking--title')}</strong>
                      </Subtitle>
                      <p>{t('mmb__agent--booking--description')}</p>
                    </LockedOut>
                  )}
                </>
              )}
              {isWhiteLabel && <WhiteLabelMessage>{t('mmb-login--contact-message')}</WhiteLabelMessage>}
            </MyBookingFormInner>
          </FormInnerWrapper>
        </MyBookingLoginForm>
        <MyBookingLoginHero imageUrl={LOGIN_HERO_URL} />
      </MyBookingLoginFormContainer>
    </MyBookingLoginContainer>
  );
};

export const MyBookingLoginPageLayout = withTheme(MyBookingLoginPageLayoutComponent);
